// geographycontent12.js
// Section Twelve — Exam Practice
// Grade9Ready Geography content module
//
// Covers:
// 168 Mixed Practice Tests
// 174 Paper 1: Physical Geography
// 177 Paper 2: Human Geography
// 180 Paper 3: Geographical Issues
// 184 Answers
// 193 Index-style revision links
//
// Designed as a large question-bank module for:
// - mixed retrieval practice
// - paper-specific exam practice
// - skills and fieldwork practice
// - short-answer, calculation, explain, assess and evaluate questions

export const geographyContent12 = {
  id: "geography-section-12",
  subject: "Geography",
  sectionNumber: 12,
  title: "Exam Practice",
  slug: "exam-practice",
  examBoardLink:
    "GCSE Geography-style practice: Paper 1 physical geography, Paper 2 human geography, Paper 3 geographical applications, fieldwork and skills",
  intro:
    "This section provides exam-style practice across the full geography course. It includes mixed retrieval questions, Paper 1 physical geography questions, Paper 2 human geography questions, and Paper 3 geographical issues, skills and fieldwork questions. The aim is to build confidence with command words, evidence use, calculations, map skills, explanation, assessment and evaluation.",
  keySkills: [
    "Recognise and respond accurately to command words.",
    "Use precise geographical vocabulary.",
    "Use evidence, examples and case-study detail.",
    "Answer short-answer, calculation, explanation and evaluation questions.",
    "Structure extended responses clearly.",
    "Interpret maps, graphs, photographs and data.",
    "Apply geographical understanding to unfamiliar contexts.",
    "Evaluate methods, evidence and reliability in fieldwork questions."
  ],

  commandWords: [
    {
      command: "Define",
      meaning: "Give the meaning of a key term.",
      examTip: "Keep it precise. Do not add unnecessary explanation."
    },
    {
      command: "Describe",
      meaning: "Say what something is like or what the pattern shows.",
      examTip: "Use data, direction, scale and place names where possible."
    },
    {
      command: "Explain",
      meaning: "Give reasons. Show how or why something happens.",
      examTip: "Use because, therefore, this means that, as a result."
    },
    {
      command: "Compare",
      meaning: "Identify similarities and differences.",
      examTip: "Mention both places, graphs or examples in the same answer."
    },
    {
      command: "Assess",
      meaning: "Consider importance, impact or extent before reaching a judgement.",
      examTip: "Give both sides and finish with a justified conclusion."
    },
    {
      command: "Evaluate",
      meaning: "Judge the success, usefulness or effectiveness of something.",
      examTip: "Discuss strengths, weaknesses and conditions affecting success."
    },
    {
      command: "Calculate",
      meaning: "Work out a numerical answer.",
      examTip: "Show working and include units if relevant."
    },
    {
      command: "Suggest",
      meaning: "Use your geographical understanding to propose a likely reason or answer.",
      examTip: "You may not have been taught the exact example, so apply logic."
    }
  ],

  answerFrameworks: {
    fourMarkExplain:
      "Point 1 → because/explanation → Point 2 → because/explanation.",
    sixMarkExplain:
      "Make 2–3 developed points. Each point should include a cause, process and impact.",
    eightMarkEvaluate:
      "Introduction or judgement line → argument for → argument against/limitations → example evidence → final justified judgement.",
    compare:
      "Use paired wording: whereas, however, both, similarly, in contrast.",
    describePattern:
      "Overall pattern → specific data/place evidence → anomaly → possible link if required.",
    fieldworkEvaluate:
      "Method strength → limitation → effect on reliability → improvement."
  },

  topics: [
    {
      id: "geo12-1-mixed-practice-tests",
      title: "Mixed Practice Tests",
      slug: "mixed-practice-tests",
      pageRef: "Source booklet p.168",
      learningObjectives: [
        "Recall knowledge from across the whole geography course.",
        "Practise switching between physical, human and skills topics.",
        "Build confidence with mixed exam-style questions."
      ],
      tutorial: [
        {
          heading: "Why mixed practice matters",
          body:
            "In the exam, you need to remember knowledge from across the course and apply it quickly. Mixed practice strengthens long-term memory because it forces you to choose the correct topic, process and command-word approach."
        },
        {
          heading: "How to use this bank",
          body:
            "Use short-answer questions for retrieval, then complete longer explain, assess and evaluate questions under timed conditions. Mark answers using the indicative content, then improve them using the Grade 9 move."
        }
      ],
      grade9Move:
        "For mixed practice, do not just recall facts. Identify the topic, command word, marks available and the type of evidence needed before writing."
    },
    {
      id: "geo12-2-paper-1-physical-geography",
      title: "Paper 1: Physical Geography",
      slug: "paper-1-physical-geography",
      pageRef: "Source booklet p.174",
      learningObjectives: [
        "Practise tectonics, weather, climate, ecosystems, rivers, coasts, glaciation and environmental processes.",
        "Explain physical processes in clear sequences.",
        "Evaluate management strategies for physical environments."
      ],
      tutorial: [
        {
          heading: "Paper 1 approach",
          body:
            "Physical geography answers often need process chains. For example: pressure builds at a plate boundary → sudden movement occurs → seismic waves spread → ground shakes → buildings collapse. Strong answers explain sequence, not just final effects."
        }
      ],
      grade9Move:
        "Use cause → process → landform/hazard → impact → management. This works across many physical geography questions."
    },
    {
      id: "geo12-3-paper-2-human-geography",
      title: "Paper 2: Human Geography",
      slug: "paper-2-human-geography",
      pageRef: "Source booklet p.177",
      learningObjectives: [
        "Practise population, urbanisation, development, industry, resources and contrasting regions.",
        "Use social, economic and environmental categories.",
        "Compare places using evidence."
      ],
      tutorial: [
        {
          heading: "Paper 2 approach",
          body:
            "Human geography answers often involve people, money, services, inequality, resources and decision-making. Strong answers balance opportunities and challenges and use specific place detail."
        }
      ],
      grade9Move:
        "Use social, economic and environmental paragraphs for human geography evaluation questions."
    },
    {
      id: "geo12-4-paper-3-geographical-issues",
      title: "Paper 3: Geographical Issues",
      slug: "paper-3-geographical-issues",
      pageRef: "Source booklet p.180",
      learningObjectives: [
        "Practise issue evaluation, unseen resources, data interpretation and fieldwork.",
        "Use evidence from maps, graphs, photos and tables.",
        "Evaluate methods and reach justified decisions."
      ],
      tutorial: [
        {
          heading: "Paper 3 approach",
          body:
            "Paper 3 questions often give unfamiliar resources. Read the resource carefully, quote evidence from it, then apply geographical understanding. Fieldwork answers should focus on method, reliability, data presentation, conclusions and improvements."
        }
      ],
      grade9Move:
        "Always use the resource. A strong Paper 3 answer combines evidence from the figure with your own geographical understanding."
    }
  ],

  questionBanks: {
    mixedPractice: [
      {
        id: "mix-001",
        topic: "Locational knowledge",
        command: "Name",
        question: "Name the seven continents.",
        marks: 2,
        answer:
          "Africa, Antarctica, Asia, Europe, North America, South America and Oceania/Australia.",
        skillFocus: "Recall"
      },
      {
        id: "mix-002",
        topic: "Tectonics",
        command: "Define",
        question: "Define the term natural hazard.",
        marks: 2,
        answer:
          "A natural event or process that threatens people, property or the environment.",
        skillFocus: "Key term"
      },
      {
        id: "mix-003",
        topic: "Weathering",
        command: "Explain",
        question: "Explain how freeze-thaw weathering breaks down rock.",
        marks: 4,
        answer:
          "Water enters cracks in rock. When temperatures fall below freezing, the water freezes and expands. This puts pressure on the rock. Repeated freezing and thawing widens the crack until pieces break away.",
        skillFocus: "Process sequence"
      },
      {
        id: "mix-004",
        topic: "Rivers",
        command: "Explain",
        question: "Explain how a waterfall retreats upstream.",
        marks: 4,
        answer:
          "Softer rock beneath harder rock is eroded faster by hydraulic action and abrasion. This creates an overhang of hard rock. The overhang collapses because it is unsupported. Repeated erosion and collapse cause the waterfall to retreat upstream.",
        skillFocus: "Landform formation"
      },
      {
        id: "mix-005",
        topic: "Coasts",
        command: "Explain",
        question: "Explain how longshore drift transports material along a beach.",
        marks: 4,
        answer:
          "Waves approach the beach at an angle because of the prevailing wind. Swash moves sediment diagonally up the beach. Backwash moves sediment straight down under gravity. Repetition moves sediment along the coastline.",
        skillFocus: "Coastal process"
      },
      {
        id: "mix-006",
        topic: "Population",
        command: "Calculate",
        question:
          "A country has a population of 30,000,000 and an area of 600,000 km². Calculate the population density.",
        marks: 2,
        answer:
          "30,000,000 ÷ 600,000 = 50 people per km².",
        skillFocus: "Numeracy"
      },
      {
        id: "mix-007",
        topic: "Migration",
        command: "Explain",
        question: "Explain the difference between push and pull factors in migration.",
        marks: 4,
        answer:
          "Push factors are negative reasons that encourage people to leave a place, such as conflict, unemployment or poor services. Pull factors are positive reasons that attract people to a place, such as jobs, safety, education and healthcare.",
        skillFocus: "Human geography concepts"
      },
      {
        id: "mix-008",
        topic: "Development",
        command: "Explain",
        question:
          "Explain why GNI per capita can be a misleading measure of development.",
        marks: 4,
        answer:
          "GNI per capita is an average, so it can hide inequality. A country may have a high average income because a small group is very wealthy, while many people remain poor. It also does not directly show health, education or quality of life.",
        skillFocus: "Evaluation of indicators"
      },
      {
        id: "mix-009",
        topic: "Resources",
        command: "Compare",
        question:
          "Compare renewable and non-renewable resources.",
        marks: 4,
        answer:
          "Renewable resources can naturally replace themselves if managed carefully, such as wind, solar energy, forests and fish stocks. Non-renewable resources cannot be replaced within a human timescale, such as coal, oil, gas and metal ores.",
        skillFocus: "Comparison"
      },
      {
        id: "mix-010",
        topic: "Environment",
        command: "Explain",
        question:
          "Explain how deforestation can increase soil erosion.",
        marks: 4,
        answer:
          "Trees protect soil because roots bind soil together and leaves intercept rainfall. When trees are removed, soil is exposed to heavy rain and wind. More runoff occurs and soil is washed or blown away more easily.",
        skillFocus: "Human-environment interaction"
      },
      {
        id: "mix-011",
        topic: "Skills",
        command: "State",
        question:
          "In a grid reference, which should be read first: eastings or northings?",
        marks: 1,
        answer:
          "Eastings.",
        skillFocus: "OS map skills"
      },
      {
        id: "mix-012",
        topic: "Graphs",
        command: "Choose",
        question:
          "Which type of graph is best for showing change over time?",
        marks: 1,
        answer:
          "Line graph.",
        skillFocus: "Data presentation"
      }
    ],

    paper1PhysicalGeography: [
      {
        id: "p1-001",
        section: "Tectonic hazards",
        command: "Describe",
        question:
          "Describe the distribution of earthquakes and volcanoes around the world.",
        marks: 4,
        answer:
          "Earthquakes and volcanoes are not evenly distributed. They are mainly found in narrow belts along tectonic plate boundaries, especially around the Pacific Ring of Fire. Some also occur along mid-ocean ridges and collision zones. Fewer occur in the centre of tectonic plates.",
        grade9Move:
          "Use pattern language: clustered, linear belts, plate margins, Pacific Ring of Fire, uneven distribution."
      },
      {
        id: "p1-002",
        section: "Tectonic hazards",
        command: "Explain",
        question:
          "Explain how earthquakes occur at conservative plate margins.",
        marks: 4,
        answer:
          "At conservative plate margins, plates slide past each other. Friction causes them to become locked. Pressure builds as the plates continue trying to move. When the pressure is suddenly released, seismic waves spread out and cause an earthquake.",
        grade9Move:
          "Mention friction, locked plates, pressure build-up and sudden release."
      },
      {
        id: "p1-003",
        section: "Tectonic hazards",
        command: "Evaluate",
        question:
          "Evaluate the effectiveness of planning and preparation in reducing the impacts of tectonic hazards.",
        marks: 8,
        answer:
          "Planning and preparation can reduce deaths and damage through earthquake-resistant buildings, hazard maps, evacuation routes, warning systems, education and emergency drills. These reduce vulnerability and improve response speed. However, they cannot stop tectonic events from happening, and poorer countries may lack money, technology or governance to prepare effectively. Some hazards, such as earthquakes, are difficult to predict accurately. Overall, planning and preparation are very important, but their success depends on wealth, infrastructure, education and government capacity.",
        grade9Move:
          "Judge effectiveness by separating reducing deaths from preventing the hazard."
      },
      {
        id: "p1-004",
        section: "Weather and climate",
        command: "Explain",
        question:
          "Explain how relief rainfall forms.",
        marks: 4,
        answer:
          "Moist air is forced to rise over high land. As it rises, it cools and condenses to form clouds. Continued condensation produces rainfall on the windward side. The leeward side may be drier because the air has lost moisture.",
        grade9Move:
          "Use windward and leeward for precise physical geography."
      },
      {
        id: "p1-005",
        section: "Climate change",
        command: "Explain",
        question:
          "Explain how human activities contribute to global warming.",
        marks: 6,
        answer:
          "Burning fossil fuels in power stations, transport and industry releases carbon dioxide. Deforestation releases stored carbon and reduces the number of trees absorbing carbon dioxide. Farming releases methane from livestock and rice fields. These greenhouse gases strengthen the enhanced greenhouse effect, trapping more heat in the atmosphere and raising average global temperatures.",
        grade9Move:
          "Name greenhouse gases and link them to the enhanced greenhouse effect."
      },
      {
        id: "p1-006",
        section: "Ecosystems",
        command: "Explain",
        question:
          "Explain why tropical rainforests have high biodiversity.",
        marks: 4,
        answer:
          "Tropical rainforests are hot and wet throughout the year, creating ideal conditions for plant growth. This provides many habitats and food sources. The layered structure of the forest creates different niches, supporting a wide variety of plants and animals.",
        grade9Move:
          "Link climate → vegetation growth → habitats/niches → biodiversity."
      },
      {
        id: "p1-007",
        section: "Ecosystems",
        command: "Assess",
        question:
          "Assess the impacts of deforestation in tropical rainforests.",
        marks: 8,
        answer:
          "Deforestation can bring economic benefits by creating land for farming, cattle ranching, mining, roads and logging. It can provide jobs and income. However, it causes serious environmental impacts including habitat loss, reduced biodiversity, soil erosion, disruption of the water cycle and increased carbon dioxide emissions. It can also affect indigenous communities and reduce long-term resource security. Overall, short-term economic gains may be outweighed by long-term environmental and social damage if forests are not managed sustainably.",
        grade9Move:
          "Balance short-term economic benefits against long-term environmental and social costs."
      },
      {
        id: "p1-008",
        section: "Rivers",
        command: "Explain",
        question:
          "Explain how meanders form.",
        marks: 6,
        answer:
          "Meanders form where a river begins to erode laterally. On the outside bend, water is deeper and faster, so hydraulic action and abrasion erode the bank, forming a river cliff. On the inside bend, water is slower and deposition occurs, forming a slip-off slope. Over time, erosion and deposition make the bend more pronounced.",
        grade9Move:
          "Use outside bend erosion and inside bend deposition together."
      },
      {
        id: "p1-009",
        section: "Rivers",
        command: "Evaluate",
        question:
          "Evaluate hard engineering as a way of reducing flood risk.",
        marks: 8,
        answer:
          "Hard engineering such as flood walls, embankments, dams and channel straightening can reduce flood risk by controlling or storing water. These methods can protect homes, businesses and infrastructure. However, they are expensive, can damage river ecosystems, may transfer flood risk downstream and require maintenance. They may also create a false sense of security. Overall, hard engineering can be effective in high-value areas, but it is often best combined with soft engineering such as floodplain zoning and wetland restoration.",
        grade9Move:
          "Judge by cost, effectiveness, environmental impact and whether risk is transferred."
      },
      {
        id: "p1-010",
        section: "Coasts",
        command: "Explain",
        question:
          "Explain the formation of a cave, arch, stack and stump.",
        marks: 6,
        answer:
          "Waves exploit weaknesses in a headland through hydraulic action and abrasion. Cracks widen into caves. Continued erosion may cause caves to meet and form an arch. Weathering and erosion weaken the arch roof until it collapses, leaving a stack. Further erosion reduces the stack to a stump.",
        grade9Move:
          "Keep the sequence in the correct order and name the erosion processes."
      },
      {
        id: "p1-011",
        section: "Coasts",
        command: "Evaluate",
        question:
          "Evaluate the use of hard engineering to protect coastlines.",
        marks: 8,
        answer:
          "Hard engineering can protect valuable property and infrastructure. Sea walls reflect wave energy, rock armour absorbs wave energy and groynes trap sediment to build beaches. However, these methods are expensive, may look unnatural, require maintenance and can increase erosion elsewhere by interrupting sediment movement. Overall, hard engineering is most justified where the value of land and infrastructure is greater than the cost and environmental impact of protection.",
        grade9Move:
          "Use cost-benefit judgement: protect high-value land, but consider knock-on erosion."
      },
      {
        id: "p1-012",
        section: "Glaciation",
        command: "Explain",
        question:
          "Explain how a U-shaped valley forms.",
        marks: 6,
        answer:
          "A glacier moves through a former V-shaped river valley. The ice erodes the valley floor and sides by plucking and abrasion. This makes the valley deeper, wider and straighter. Interlocking spurs may be cut off to form truncated spurs. When the ice melts, a steep-sided valley with a flat floor remains.",
        grade9Move:
          "Mention the original V-shaped valley and how ice changes its shape."
      },
      {
        id: "p1-013",
        section: "Weathering",
        command: "Compare",
        question:
          "Compare freeze-thaw weathering and biological weathering.",
        marks: 4,
        answer:
          "Freeze-thaw weathering happens when water enters cracks, freezes, expands and breaks rock apart. Biological weathering is caused by living things, such as plant roots growing into cracks and forcing them wider. Both break rock down, but they work through different processes.",
        grade9Move:
          "State similarity and difference explicitly."
      },
      {
        id: "p1-014",
        section: "Environmental issues",
        command: "Explain",
        question:
          "Explain the causes and effects of acid rain.",
        marks: 6,
        answer:
          "Acid rain is caused when sulphur dioxide and nitrogen oxides from power stations, vehicles and industry react with moisture in the atmosphere. These pollutants may be carried by winds before falling as acidic rain. Acid rain can damage forests, acidify lakes, kill fish, reduce soil fertility and chemically weather limestone or marble buildings.",
        grade9Move:
          "Use source → gases → atmospheric reaction → rainfall → impacts."
      },
      {
        id: "p1-015",
        section: "Natural hazards",
        command: "Assess",
        question:
          "Assess why people continue to live in areas at risk from natural hazards.",
        marks: 8,
        answer:
          "People continue to live in hazard-prone areas because of jobs, family ties, fertile soils, tourism, trade and access to resources. Volcanoes can create fertile soils and geothermal energy. Coastal areas may provide fishing, ports and tourism. Some people cannot afford to move, while others trust prediction, planning and building design. However, living in hazard zones can expose people to death, injury and economic loss. Overall, people remain because benefits, attachment and limited alternatives often outweigh perceived risk.",
        grade9Move:
          "Balance risk with benefits and capacity to cope."
      }
    ],

    paper2HumanGeography: [
      {
        id: "p2-001",
        section: "Population",
        command: "Explain",
        question:
          "Explain why population distribution is uneven.",
        marks: 6,
        answer:
          "Population distribution is uneven because physical and human factors vary. Flat land, fertile soils, reliable water and mild climates attract settlement because farming, building and transport are easier. Harsh climates, steep mountains, deserts and dense rainforest are less attractive. Human factors such as jobs, transport links, services, trade and history also concentrate people in some areas.",
        grade9Move:
          "Use both physical and human factors."
      },
      {
        id: "p2-002",
        section: "Population",
        command: "Calculate",
        question:
          "A region has a population of 2,500,000 and an area of 5,000 km². Calculate the population density.",
        marks: 2,
        answer:
          "2,500,000 ÷ 5,000 = 500 people per km².",
        grade9Move:
          "Always include the unit: people per km²."
      },
      {
        id: "p2-003",
        section: "Population structure",
        command: "Explain",
        question:
          "Explain how a population pyramid can help governments plan.",
        marks: 6,
        answer:
          "A population pyramid shows the age and sex structure of a population. If there are many children, governments may need more schools, teachers and future jobs. If there are many older people, governments may need more healthcare, pensions and social care. This helps plan spending, housing and infrastructure.",
        grade9Move:
          "Link age groups to specific services."
      },
      {
        id: "p2-004",
        section: "Migration",
        command: "Assess",
        question:
          "Assess the impacts of migration on origin and destination areas.",
        marks: 8,
        answer:
          "Migration can benefit origin areas through remittances sent home by migrants, reducing poverty and supporting families. However, origin areas may lose young workers and skilled people. Destination areas may gain workers, skills, cultural diversity and economic growth. However, rapid migration can increase pressure on housing, schools, healthcare and infrastructure. Overall, migration creates both benefits and challenges, and impacts depend on the scale of migration and how well it is managed.",
        grade9Move:
          "Structure by origin positives, origin negatives, destination positives, destination negatives."
      },
      {
        id: "p2-005",
        section: "Urbanisation",
        command: "Explain",
        question:
          "Explain why urbanisation is rapid in many LICs and NEEs.",
        marks: 6,
        answer:
          "Urbanisation is rapid because rural areas may have poverty, limited services and few jobs, pushing people to leave. Cities attract people with employment, education, healthcare and better services. Natural increase in cities can also be high, increasing urban populations further.",
        grade9Move:
          "Separate rural push, urban pull and natural increase."
      },
      {
        id: "p2-006",
        section: "Urban issues",
        command: "Evaluate",
        question:
          "Evaluate strategies used to improve informal settlements.",
        marks: 8,
        answer:
          "Strategies include self-help schemes, site-and-service schemes, legal land tenure, water pipes, toilets, drainage, electricity and road improvements. These can improve health, safety and quality of life while keeping communities together. However, improvements can be slow, expensive and difficult if land ownership is unclear or settlements are on risky land. Overall, upgrading is often more sustainable than demolition if residents are involved and basic services are provided.",
        grade9Move:
          "Mention why demolition does not solve the underlying housing shortage."
      },
      {
        id: "p2-007",
        section: "Development",
        command: "Explain",
        question:
          "Explain why development is difficult to measure accurately.",
        marks: 6,
        answer:
          "Development is difficult to measure because it includes income, health, education, safety, equality and quality of life. Single indicators are limited. GNI per capita shows average income but hides inequality. Life expectancy and literacy show social progress but not everything. A range of indicators gives a more accurate picture.",
        grade9Move:
          "Always mention averages and hidden inequality."
      },
      {
        id: "p2-008",
        section: "Development",
        command: "Evaluate",
        question:
          "Evaluate the use of aid in reducing the development gap.",
        marks: 8,
        answer:
          "Aid can reduce the development gap by providing emergency relief, clean water, healthcare, education, food and infrastructure. Long-term aid can improve skills and self-reliance. However, aid may fail if corruption occurs, if it creates dependency or if it is tied to conditions that benefit donor countries. Overall, aid is most effective when it is targeted, transparent, locally appropriate and supports long-term independence.",
        grade9Move:
          "Separate emergency aid from long-term development aid."
      },
      {
        id: "p2-009",
        section: "World trade",
        command: "Explain",
        question:
          "Explain how world trade can both help and hinder development.",
        marks: 8,
        answer:
          "World trade can help development by creating jobs, increasing income, attracting investment and raising tax revenue. However, it can hinder development if poorer countries rely on low-value primary products with unstable prices while paying high prices for manufactured goods. Unequal trade can make it hard to invest in services and infrastructure. Fairer trade and diversification can improve benefits.",
        grade9Move:
          "Compare raw materials with value-added manufactured goods."
      },
      {
        id: "p2-010",
        section: "Industry",
        command: "Explain",
        question:
          "Explain how employment structure changes as countries develop.",
        marks: 6,
        answer:
          "In less developed economies, many people may work in primary industries such as farming and mining. As industrialisation occurs, secondary manufacturing grows. Later, as incomes rise and technology improves, tertiary and quaternary sectors expand because demand for services, research, technology and information work increases.",
        grade9Move:
          "Use sequence: primary → secondary → tertiary/quaternary."
      },
      {
        id: "p2-011",
        section: "UK industry",
        command: "Assess",
        question:
          "Assess the impacts of deindustrialisation in the UK.",
        marks: 8,
        answer:
          "Deindustrialisation caused job losses, unemployment, lower incomes and derelict land in some former industrial areas. It also affected community identity where towns depended on one industry. However, some areas have regenerated brownfield sites into housing, offices, shops, leisure spaces and transport hubs. New service and high-tech jobs have created opportunities, but benefits are uneven. Overall, impacts depend on how successfully places adapt and attract new investment.",
        grade9Move:
          "Mention uneven regional impacts."
      },
      {
        id: "p2-012",
        section: "Resources",
        command: "Evaluate",
        question:
          "Evaluate renewable energy as a solution to future energy demand.",
        marks: 8,
        answer:
          "Renewable energy reduces reliance on finite fossil fuels and produces lower emissions during operation. It can improve energy security where countries have local wind, solar, hydroelectric or geothermal resources. However, some renewables are intermittent, expensive to build, location-specific and may require storage or grid upgrades. Overall, renewables are essential for reducing emissions, but most countries need a balanced energy mix.",
        grade9Move:
          "Judge using reliability, cost, emissions and energy security."
      },
      {
        id: "p2-013",
        section: "Nigeria and Japan",
        command: "Compare",
        question:
          "Compare the population challenges facing Nigeria and Japan.",
        marks: 6,
        answer:
          "Nigeria has a youthful and rapidly growing population, creating pressure for schools, jobs, housing, healthcare and infrastructure. This could become an opportunity if young people are educated and employed. Japan has an ageing population caused by high life expectancy and low fertility, creating pressure on pensions, healthcare and the workforce. Nigeria’s challenge is rapid growth, whereas Japan’s is ageing and possible decline.",
        grade9Move:
          "Use whereas to keep comparison explicit."
      },
      {
        id: "p2-014",
        section: "Lagos and Osaka",
        command: "Assess",
        question:
          "Assess the urban challenges facing Lagos and Osaka.",
        marks: 8,
        answer:
          "Lagos faces rapid urban growth, informal settlements, congestion, pollution, waste issues and pressure on water, sanitation and housing. These challenges are linked to growth outpacing infrastructure. Osaka has stronger infrastructure and higher development, but faces congestion, high land values, ageing population and earthquake risk. Both cities face urban challenges, but Lagos is more affected by rapid growth and service pressure, while Osaka is more affected by ageing, cost and hazard planning.",
        grade9Move:
          "Do not simply say Osaka is better. Compare the type of challenge."
      },
      {
        id: "p2-015",
        section: "Water",
        command: "Evaluate",
        question:
          "Evaluate strategies used to manage water demand and supply.",
        marks: 8,
        answer:
          "Supply can be increased using reservoirs, dams, water transfers and desalination. These can improve reliability but are expensive and may damage ecosystems or communities. Demand can be reduced through water meters, fixing leaks, recycling, efficient irrigation and public education. These are often more sustainable but require investment and behaviour change. The best approach usually combines supply management with demand reduction.",
        grade9Move:
          "Separate supply-side and demand-side strategies."
      }
    ],

    paper3GeographicalIssues: [
      {
        id: "p3-001",
        section: "Issue evaluation",
        command: "Describe",
        question:
          "Describe how to use evidence from a resource booklet in an issue evaluation answer.",
        marks: 4,
        answer:
          "Use specific evidence from the resource, such as data values, map locations, trends or quotes. Link the evidence to the question rather than copying it. Combine resource evidence with geographical understanding to support a judgement.",
        grade9Move:
          "Quote the figure without over-copying: ‘Figure 2 shows… therefore…’"
      },
      {
        id: "p3-002",
        section: "Decision making",
        command: "Evaluate",
        question:
          "A city is deciding whether to build a new reservoir. Evaluate the possible advantages and disadvantages.",
        marks: 8,
        answer:
          "A reservoir can store water, improve supply during dry periods, support homes, industry and farming, and may provide recreation or hydroelectric power. However, it can flood farmland, habitats or settlements, disrupt river ecosystems, cost a lot to build and create conflict with local communities. Overall, a reservoir may be justified if water demand is high and alternatives such as reducing leaks or water transfer are insufficient, but environmental and social impacts must be managed.",
        grade9Move:
          "Make a final decision based on evidence, not just a list of pros and cons."
      },
      {
        id: "p3-003",
        section: "Fieldwork methods",
        command: "Explain",
        question:
          "Explain how systematic sampling could be used in a fieldwork enquiry.",
        marks: 4,
        answer:
          "Systematic sampling means collecting data at regular intervals. For example, a student could measure environmental quality every 100 metres along a transect through a town. This gives even coverage and reduces the chance of only choosing convenient locations.",
        grade9Move:
          "Name the interval and link it to reliability."
      },
      {
        id: "p3-004",
        section: "Fieldwork reliability",
        command: "Evaluate",
        question:
          "Evaluate the reliability of a pedestrian count carried out at one location for five minutes.",
        marks: 6,
        answer:
          "The method provides primary quantitative data and is quick to complete. However, reliability is limited because one location and five minutes may not represent the whole area or different times of day. Results could be affected by weather, school finishing times or temporary events. Reliability could be improved by using more sites, longer counts, repeated counts and different times of day.",
        grade9Move:
          "Explain how each limitation affects reliability."
      },
      {
        id: "p3-005",
        section: "Data presentation",
        command: "Choose",
        question:
          "Choose a suitable graph to show changes in river width downstream and explain your choice.",
        marks: 4,
        answer:
          "A line graph would be suitable because river width is continuous data and the enquiry is showing change with distance downstream. The line makes it easy to identify trends, increases, decreases and anomalies.",
        grade9Move:
          "Justify the graph using the data type."
      },
      {
        id: "p3-006",
        section: "Map skills",
        command: "Explain",
        question:
          "Explain how contour lines help identify suitable routes for a footpath.",
        marks: 4,
        answer:
          "Contour lines show height and slope steepness. Close contours show steep slopes, which may be harder or less safe for a footpath. Wider-spaced contours show gentler slopes, which may be easier for walkers. Route planners can use this to avoid very steep ground.",
        grade9Move:
          "Link contour spacing to decision-making."
      },
      {
        id: "p3-007",
        section: "Photo interpretation",
        command: "Suggest",
        question:
          "Suggest two signs from a photograph that an area may be affected by deprivation.",
        marks: 4,
        answer:
          "Possible signs include poorly maintained buildings, litter, graffiti, boarded-up shops, lack of green space, damaged roads, overcrowded housing or limited services. These suggest lower investment and poorer environmental quality.",
        grade9Move:
          "Use cautious language: ‘may suggest’ rather than claiming certainty from one photo."
      },
      {
        id: "p3-008",
        section: "Unseen resource",
        command: "Describe",
        question:
          "A graph shows carbon emissions rising from 1990 to 2020, with a small fall after 2015. Describe the trend.",
        marks: 3,
        answer:
          "Carbon emissions increased overall from 1990 to 2020. The trend was upward for most of the period, but there was a slight decrease after 2015. This suggests emissions remained high despite the recent fall.",
        grade9Move:
          "Mention overall trend and change within the trend."
      },
      {
        id: "p3-009",
        section: "Data interpretation",
        command: "Explain",
        question:
          "Explain why a scatter graph may show correlation but not prove causation.",
        marks: 4,
        answer:
          "A scatter graph can show that two variables are related, such as both increasing together. However, it does not prove that one variable directly causes the other. A third factor may influence both variables, or the relationship may be coincidental. Further evidence is needed to prove causation.",
        grade9Move:
          "Use the phrase ‘correlation does not prove causation’ and explain why."
      },
      {
        id: "p3-010",
        section: "Fieldwork conclusions",
        command: "Explain",
        question:
          "Explain how a student should write a conclusion for a fieldwork enquiry.",
        marks: 6,
        answer:
          "A conclusion should answer the original enquiry question using evidence from the data. It should describe the main pattern, use specific results, mention whether the hypothesis was supported and identify any anomalies. It should avoid unsupported opinions and should link findings back to geographical understanding.",
        grade9Move:
          "Conclusion = answer + evidence + hypothesis + anomaly + geographical reason."
      },
      {
        id: "p3-011",
        section: "Issue evaluation",
        command: "Assess",
        question:
          "Assess whether a new coastal defence scheme should be built in a town at risk from erosion.",
        marks: 8,
        answer:
          "A coastal defence scheme may protect homes, roads, businesses and tourism, reducing economic losses and giving residents security. It may be justified if the land value is high. However, hard engineering can be expensive, visually intrusive and may increase erosion further along the coast by interrupting sediment movement. Soft engineering may be cheaper and more sustainable but less protective. Overall, the decision depends on cost, land value, environmental impact and whether the scheme transfers risk elsewhere.",
        grade9Move:
          "Include a clear final judgement based on cost-benefit and sustainability."
      },
      {
        id: "p3-012",
        section: "Fieldwork evaluation",
        command: "Evaluate",
        question:
          "Evaluate the use of questionnaires in human geography fieldwork.",
        marks: 8,
        answer:
          "Questionnaires can collect useful primary data about opinions, behaviour and perceptions. They can be quick and can produce both quantitative and qualitative data. However, results may be biased if the sample is small or unrepresentative, if questions are leading, or if people misunderstand them. Some people may refuse to answer or give inaccurate responses. Reliability can be improved by using neutral questions, a larger sample, different locations and different times of day.",
        grade9Move:
          "Always link limitations to bias and representativeness."
      }
    ]
  },

  fullPracticePapers: [
    {
      id: "full-paper-1",
      title: "Practice Paper 1 — Physical Geography",
      recommendedTimeMinutes: 45,
      totalMarks: 40,
      questions: [
        "Define natural hazard. [2]",
        "Describe the global distribution of volcanoes. [4]",
        "Explain how earthquakes occur at conservative plate margins. [4]",
        "Explain how relief rainfall forms. [4]",
        "Explain how human activities contribute to global warming. [6]",
        "Explain the formation of a waterfall and gorge. [6]",
        "Evaluate the use of hard engineering to protect coastlines. [8]",
        "Explain how a U-shaped valley forms. [6]"
      ],
      markSchemeReference:
        "Use the relevant answers from paper1PhysicalGeography question bank."
    },
    {
      id: "full-paper-2",
      title: "Practice Paper 2 — Human Geography",
      recommendedTimeMinutes: 45,
      totalMarks: 40,
      questions: [
        "Calculate population density for a region with 2,500,000 people and an area of 5,000 km². [2]",
        "Explain why population distribution is uneven. [6]",
        "Explain why urbanisation is rapid in many LICs and NEEs. [6]",
        "Assess the impacts of migration on origin and destination areas. [8]",
        "Explain why development is difficult to measure accurately. [6]",
        "Evaluate renewable energy as a solution to future energy demand. [8]",
        "Compare the population challenges facing Nigeria and Japan. [6]",
        "State one example of a quaternary industry. [1]",
        "Define deindustrialisation. [1]"
      ],
      markSchemeReference:
        "Use the relevant answers from paper2HumanGeography question bank."
    },
    {
      id: "full-paper-3",
      title: "Practice Paper 3 — Geographical Issues and Skills",
      recommendedTimeMinutes: 45,
      totalMarks: 40,
      questions: [
        "Describe how to use evidence from a resource booklet in an issue evaluation answer. [4]",
        "Explain how systematic sampling could be used in a fieldwork enquiry. [4]",
        "Evaluate the reliability of a pedestrian count carried out at one location for five minutes. [6]",
        "Choose a suitable graph to show changes in river width downstream and explain your choice. [4]",
        "Explain how contour lines help identify suitable routes for a footpath. [4]",
        "Explain why a scatter graph may show correlation but not prove causation. [4]",
        "Evaluate the use of questionnaires in human geography fieldwork. [8]",
        "Explain how a student should write a conclusion for a fieldwork enquiry. [6]"
      ],
      markSchemeReference:
        "Use the relevant answers from paper3GeographicalIssues question bank."
    }
  ],

  revisionIndex: [
    {
      keyword: "Abrasion",
      sections: ["Rivers", "Coasts", "Glaciation"],
      hint:
        "Scraping erosion by sediment or rocks carried by water, waves or ice."
    },
    {
      keyword: "Aid",
      sections: ["Development"],
      hint:
        "Can be emergency, development, bilateral, multilateral, voluntary or tied."
    },
    {
      keyword: "Birth rate",
      sections: ["Population"],
      hint:
        "Number of live births per 1,000 people per year."
    },
    {
      keyword: "Climate change",
      sections: ["Weather and climate", "Influencing the Environment"],
      hint:
        "Long-term changes in temperature, rainfall and climate patterns."
    },
    {
      keyword: "Deindustrialisation",
      sections: ["Economic Activity"],
      hint:
        "Decline of manufacturing and heavy industry."
    },
    {
      keyword: "Ecosystem",
      sections: ["Biomes", "Influencing the Environment"],
      hint:
        "Living organisms interacting with each other and the physical environment."
    },
    {
      keyword: "HDI",
      sections: ["Development"],
      hint:
        "Composite development indicator using income, education and life expectancy."
    },
    {
      keyword: "Longshore drift",
      sections: ["Coasts"],
      hint:
        "Sediment movement along the beach by angled swash and straight backwash."
    },
    {
      keyword: "Natural increase",
      sections: ["Population"],
      hint:
        "Population growth when birth rate is higher than death rate."
    },
    {
      keyword: "Sustainability",
      sections: ["Resources", "Development", "Environment"],
      hint:
        "Meeting present needs without damaging future generations' ability to meet theirs."
    },
    {
      keyword: "Urbanisation",
      sections: ["Population and Urbanisation", "Contrasting Regions"],
      hint:
        "Increase in the proportion of people living in towns and cities."
    },
    {
      keyword: "Watershed",
      sections: ["Rivers"],
      hint:
        "Boundary between drainage basins."
    }
  ],

  sectionReview: {
    title: "Section Twelve Review — Exam Practice",
    summary:
      "Exam practice brings the whole geography course together. Strong answers depend on understanding the command word, using accurate geographical vocabulary, applying evidence and structuring explanations clearly. Physical geography often needs process chains. Human geography often needs social, economic and environmental balance. Geographical issues and fieldwork questions require careful use of resources, data, methods and evaluation.",
    mustKnow: [
      "Read the command word before writing.",
      "Use the number of marks to judge answer length.",
      "Define questions need precise meanings.",
      "Describe questions need patterns, data and evidence.",
      "Explain questions need reasons and process chains.",
      "Assess and evaluate questions need balanced judgement.",
      "Physical geography answers should use process sequence.",
      "Human geography answers should use social, economic and environmental categories.",
      "Paper 3 answers should use evidence from resources.",
      "Fieldwork answers should mention method, sampling, reliability, data presentation, conclusion and evaluation.",
      "Calculations should show working and units.",
      "Comparison answers must mention both sides."
    ],
    highValueExamPhrases: [
      "This leads to...",
      "As a result...",
      "This increases the risk of...",
      "This creates both opportunities and challenges...",
      "However, the impact depends on...",
      "The evidence suggests...",
      "This method may be unreliable because...",
      "A more sustainable approach would be...",
      "Overall, the most effective strategy is...",
      "Whereas one place..., the other place..."
    ],
    finalExamAdvice:
      "For every longer answer, plan quickly using three words: process, evidence, judgement. Process explains how geography works. Evidence proves your point. Judgement turns a good answer into a high-level answer."
  }
};

export default geographyContent12;